import { createTheme } from '@mui/material/styles';
import type { ClubCode, ReportStatus } from './api';

/** 프로젝트 기본색: 밝은 노랑 그라데이션 */
export const YELLOW_GRADIENT = 'linear-gradient(120deg,#fff3a3 0%,#ffe066 45%,#ffc928 100%)';
export const ON_YELLOW = '#3b2f00';
export const C = {
  ink: '#1f1d16',
  sub: '#6b6450',
  line: '#ece4c4',
  bg: '#fffdf4',
  warn: '#c2410c', warnBg: '#ffedd5',      // 주의/대기 = 주황
  ok: '#a87400', okBg: '#fff3cf',          // 발급/완료 = 앰버(금색)
  err: '#b91c1c', errBg: '#fee2e2',
  info: '#1d4ed8', infoBg: '#e0e7ff',
  gray: '#6b7280', grayBg: '#f3f4f6',
};

export const yellowTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#f5b800', dark: '#d99a00', light: '#ffe066', contrastText: ON_YELLOW },
    secondary: { main: '#a87400' },
    warning: { main: '#c2410c' },
    background: { default: C.bg, paper: '#ffffff' },
    text: { primary: C.ink, secondary: C.sub },
  },
  typography: {
    fontFamily: '"Noto Sans KR","Malgun Gothic","Apple SD Gothic Neo",sans-serif',
    button: { textTransform: 'none', fontWeight: 700, letterSpacing: 0 },
  },
  shape: { borderRadius: 10 },
  components: {
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          background: YELLOW_GRADIENT,
          color: ON_YELLOW,
          boxShadow: '0 1px 0 rgba(0,0,0,.06)',
          '&:hover': { background: 'linear-gradient(120deg,#ffef80 0%,#ffd84a 45%,#ffbf00 100%)' },
          '&.Mui-disabled': { background: '#f1ecd8', color: '#b4ab8c' },
        },
        outlinedPrimary: { color: '#7a5600', borderColor: '#e8c75a', '&:hover': { borderColor: '#d99a00', background: '#fff8d6' } },
        textPrimary: { color: '#7a5600' },
      },
    },
    MuiChip: { styleOverrides: { root: { fontWeight: 600 } } },
    MuiTab: { styleOverrides: { root: { textTransform: 'none', fontWeight: 700, minHeight: 48 } } },
    MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
  },
});

export const CLUBS: Array<{ code: ClubCode; label: string; no: number }> = [
  { code: 'PUGGLES', label: 'PUGGLES', no: 1 },
  { code: 'CUBBIES', label: 'CUBBIES', no: 2 },
  { code: 'SPARKS', label: 'SPARKS', no: 3 },
  { code: 'T&T', label: 'T&T', no: 4 },
  { code: 'TREK', label: 'TREK', no: 5 },
  { code: 'JOURNEY', label: 'JOURNEY', no: 6 },
];
/** 그룹만 알 때 */
export const CLUB_GROUPS: Array<{ code: ClubCode; label: string; no: number }> = [
  { code: 'PC', label: 'PUGGLES·CUBBIES (구분 없음)', no: 2 },
  { code: 'CM', label: 'SPARKS·T&T (CM, 구분 없음)', no: 4 },
];
export const clubLabel = (code?: string) =>
  [...CLUBS, ...CLUB_GROUPS].find((c) => c.code === code)?.label.replace(/ \(.*\)$/, '') || code || '';

export const STATUS_LABEL: Record<ReportStatus, string> = {
  submitted: '접수 대기', received: '본부 접수', checking: '확인 중', approved: '발급', rejected: '반려',
};
export const STATUS_COLOR: Record<ReportStatus, { fg: string; bg: string }> = {
  submitted: { fg: C.warn, bg: C.warnBg },
  received: { fg: C.info, bg: C.infoBg },
  checking: { fg: C.info, bg: C.infoBg },
  approved: { fg: C.ok, bg: C.okBg },
  rejected: { fg: C.err, bg: C.errBg },
};

/** 3단계 암송 10개 (부록 K) */
export const VERSES = ['딤후 2:15', 'Awana의 기도', '고전 15:3-4', '요 3:16', '계 4:8b', '롬 3:23', '롬 6:23', '롬 5:8', '롬 4:25', '행 16:31'];

const pad = (n: number) => String(n).padStart(2, '0');
const kst = (d: string | Date) => new Date(new Date(d).getTime() + 9 * 3600 * 1000);
export const ymd = (d?: string | Date | null) => {
  if (!d) return '';
  const k = kst(d);
  return `${k.getUTCFullYear()}-${pad(k.getUTCMonth() + 1)}-${pad(k.getUTCDate())}`;
};
export const ymdhm = (d?: string | Date | null) => {
  if (!d) return '';
  const k = kst(d);
  return `${ymd(d)} ${pad(k.getUTCHours())}:${pad(k.getUTCMinutes())}`;
};
export const mdhm = (d?: string | Date | null) => (d ? ymdhm(d).slice(5) : '');
const MON = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
/** 수료증 날짜 'MAR 10 2024' */
export const certDate = (d: string | Date) => {
  const k = kst(d);
  return `${MON[k.getUTCMonth()]} ${k.getUTCDate()} ${k.getUTCFullYear()}`;
};
export const todayYmd = () => ymd(new Date());

export const formatPhoneInput = (value: string) => {
  const n = value.replace(/\D/g, '').slice(0, 11);
  if (n.length <= 3) return n;
  if (n.length <= 7) return `${n.slice(0, 3)}-${n.slice(3)}`;
  return `${n.slice(0, 3)}-${n.slice(3, 7)}-${n.slice(7)}`;
};

/** 한글 이름 한 글자씩 띄우기 "김 하 늘" (영문/공백 포함 이름은 그대로) */
export const spacedKoreanName = (name: string) => (/^[가-힣]{2,5}$/.test(name) ? name.split('').join(' ') : name);

export const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};

export const toCsv = (rows: Array<Array<string | number>>) =>
  '﻿' + rows.map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\r\n');

/** 영문 이름: 영어 대문자·공백·하이픈·점만 (수료증 필수) */
/** 입력은 대문자로만 바꾸고(한글은 지우지 않음) 형식 검사는 isValidEnName 으로 안내 */
export const toEnName = (v: string) => v.toUpperCase().replace(/\s{2,}/g, ' ');
export const isValidEnName = (v?: string) => /^[A-Z][A-Z .'-]*$/.test(String(v || '').trim().toUpperCase());
